import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { guideForPath, guideUrl } from '@/lib/guides';

/**
 * The small "Guide" link: a book icon and one word, opening the matching
 * knowledge-base article in a new tab. Every page header, settings section and
 * Integrations drawer uses this one component, so they look and behave alike.
 *
 * Pass `href` (already resolved), or `guide` (a key in lib/guides), or `path`
 * (a pathname, matched on the longest route). Renders nothing when there is no
 * article: a link to a 404 is worse than no link.
 *
 * The workspace kind picks between a store and a lead-business article where
 * the map holds both. It is read from the org settings ALREADY in the cache,
 * never fetched: a header must not start a request (the Agency console has no
 * workspace to ask).
 */
export function GuideLink({ href, guide, path, label = 'Guide', className, variant = 'quiet' }) {
  const qc = useQueryClient();
  const values = qc.getQueryData(['org-settings'])?.values;
  const kind = values?.['features.ecommerce'] === true ? 'store' : values ? 'leadgen' : 'store';
  const url = href || (guide ? guideUrl(guide, kind) : null) || (path ? guideForPath(path, kind) : null);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title="Open the step-by-step guide in a new tab"
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-md text-[13px] font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        variant === 'button'
          ? 'h-8 border border-border bg-background px-3 text-foreground hover:bg-muted'
          : 'px-1.5 py-0.5 text-muted-foreground hover:bg-primary/10 hover:text-primary',
        className,
      )}
    >
      <BookOpen className="size-3.5" />
      {label}
    </a>
  );
}

export default GuideLink;
